"use client";

import React, { useState, useRef } from "react";
import styled from "styled-components";

import Box from "../Box";
import Flex from "../Flex";
import Text from "../Text";
import ImageView from "../ImageView";
import Icon from "../Icon";
import Button from "../Button";
import PopupMenu from "../PopupMenu";
import Grid from "../Grid";

import pluralize from "../../helpers/pluralize";
import { PrivacyLevel } from "../../constants/types";

import useSpaceThemeName from "../../hooks/useSpaceThemeName";
import { Kit, Space, SpaceThemeNames, Portal } from "../../models";

import { getColor } from "../utils";

import { formatDistanceToNow } from "date-fns";

type Props = {
  id: string;
  kit: Kit;
  space: { access?: Pick<Space["access"], "permissions"> };
  isSelected?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onDoubleClick?: (e: React.MouseEvent) => void;
  onPillClick?: (kit: Kit, portal: Portal) => void;

  /**
   * If provided, the overflow button is only displayed and the menuItems
   * are presented in a PopupMenu on click.
   *
   * This should be a node to populate a PopupMenu (i.e. [PopupMenu.Item])
   */
  menuItems?: React.ReactNode;
  setFavorited?: (id: Pick<Kit, "kitId">, favorited: boolean) => void;
  identifier?: string;
  overrideTheme?: boolean; // explicitly force ignore space theme a la dashboard
  selectionProps?: Record<string, string>; // eg {data-portalitemid: "123"}
  fullWidth?: boolean;
};

type WrapperProps = {
  dragStyle?: string;
  showOverlay?: boolean;
  shadowOnHover?: boolean;
};
export const KitCardWrapper = styled(Flex)<WrapperProps>`
  flex-direction: column;
  cursor: pointer;
  background-color: ${getColor("white")};
  transition:
    box-shadow 0.3s ease,
    background-color 0.3s ease;
  user-select: none;

  &:before,
  &:after {
    top: 0;
    content: "";
    height: 100%;
    width: 4px;
    background: ${props => props.theme.primaryColor};
    position: absolute;
    border-radius: 4px;
  }

  &:after {
    right: -12px;
    ${props =>
      props.dragStyle !== "right" &&
      `
      opacity: 0;
      display: none;
    `}
  }
  &:before {
    left: -12px;
    ${props =>
      props.dragStyle !== "left" &&
      `
      opacity: 0;
      display: none;
    `}
  }
  ${({ theme }) =>
    theme.themeName === SpaceThemeNames.wyman &&
    `
    height: 100%;
    &:hover {
    background-color: ${getColor("grayLightest")}
    }`}

  .hover {
    opacity: ${props => (props.showOverlay ? "1" : "0")};
    background: linear-gradient(
      180deg,
      rgba(42, 42, 42, 0) 0%,
      rgba(42, 42, 42, 0.45) 36.98%,
      rgba(42, 42, 42, 0.9) 88.02%
    );
    transition: opacity 0.3s ease;
  }
  &:hover {
    box-shadow: ${props =>
      props.shadowOnHover
        ? "0px 4px 16px 0px rgba(0, 0, 0, 0.07), 0px 1.541px 5.096px 0px rgba(0, 0, 0, 0.04), 0px 0.326px 1.304px 0px rgba(0, 0, 0, 0.03);"
        : "none"};
    .hover {
      opacity: 1;
    }
  }
`;

export const KitDescription = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const iconMap = {
  [PrivacyLevel.public]: "globe",
  [PrivacyLevel.private]: "users",
  [PrivacyLevel.password]: "globe-lock",
};

type SelectionProps = {
  isSelected?: boolean;
};
const SelectionBorder = styled(Box).attrs<SelectionProps>(props => {
  return {
    position: "absolute",
    top: -1,
    left: -1,
    right: -1,
    bottom: -1,
    border: props.isSelected
      ? `2px solid ${getColor(props.theme.primaryColor, "primary")}`
      : "2px solid transparent",
    transition: "border 0.3s ease",
  };
})<SelectionProps>`
  pointer-events: none;
`;

const PillWrapper = styled(Flex).attrs(() => {
  return {
    py: "xs",
    px: "s",
    borderRadius: "4px",
    background: getColor("grayLighter"),
    maxWidth: "120px",
    justifyContent: "center",
    alignItems: "center",
  };
})`
  &:hover {
    background: ${getColor("grayLight")};
  }
`;

const ResponsiveKitCardWrapper = styled(KitCardWrapper)`
  @media (max-width: 560px) {
    padding: 16px;
  }
`;

const ResponsiveGrid = styled(Grid)`
  @media (max-width: 560px) {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

const ResponsiveImageWrapper = styled(Flex)`
  @media (max-width: 560px) {
    width: 100%;
  }
`;

const ResponsiveContentWrapper = styled(Flex)`
  @media (max-width: 560px) {
    width: 100%;
    flex-direction: column;
  }
`;

/**
 *
 * Renders a kit card including an image, name, and description.
 *
 * The buttons on the card are shown based on the provided props:
 * Favorite button: setFavorited && kit.access.permissions.includes("favorite")
 * Overflow button: menuItems
 * @param {Object} props
 */
const KitCard: React.FC<Props> = ({
  id,
  kit,
  space,
  onClick,
  onDoubleClick,
  onPillClick,
  isSelected,
  setFavorited,
  menuItems,
  identifier,
  overrideTheme,
  fullWidth,
  selectionProps,
}) => {
  let themeName = useSpaceThemeName();
  if (overrideTheme) {
    themeName = SpaceThemeNames.classic;
  }

  const kitPortals = kit?.portals;
  const dragRef = useRef(null);
  const permissions = kit.access.permissions || [],
    spacePermissions = ((space || {}).access || {}).permissions || [],
    canFavorite = setFavorited && permissions.includes("favorite"),
    canViewMembers = spacePermissions.includes("view_members"),
    isFavorited = kit.access.isFavorite;

  const [menuVisible, setMenuVisible] = useState(false);
  const menuId = identifier
    ? `${kit.kitId}-${identifier}-overflow-menu`
    : `${kit.kitId}-overflow-menu`;
  const menu =
    menuVisible && menuItems ? (
      <PopupMenu
        source={menuId}
        data-testid="kit-card-menu"
        close={() => setMenuVisible(!menuVisible)}>
        {menuItems}
      </PopupMenu>
    ) : null;

  const handleClick = (e: React.MouseEvent) => {
    onClick(e);
  };

  const handlePillClick = (e: React.MouseEvent, kit: Kit, portal: Portal) => {
    e.stopPropagation();
    onPillClick(kit, portal);
  };

  const buttonThemeVals = {
    [SpaceThemeNames.classic]: { themeOverrides: { primaryColor: "black" } },
    [SpaceThemeNames.scher]: { themeOverrides: { primaryColor: "white" } },
    [SpaceThemeNames.wyman]: {
      themeOverrides: { primaryColor: "black" },
    },
  };

  const buttonOverrides = buttonThemeVals[themeName];

  function renderFavoriteButton() {
    return (
      <Button
        id="favorite-button"
        buttonStyle="tertiary"
        icon={isFavorited ? "star-filled" : "star"}
        m="none"
        ml={fullWidth ? "none" : "xs"}
        p="none"
        width="24"
        height="24"
        background="none"
        onClick={e => {
          e.stopPropagation();
          setFavorited(kit, !isFavorited);
        }}
        {...buttonOverrides}
      />
    );
  }

  function renderOverflowButton() {
    return (
      <Button
        id="overflow-button"
        data-popup-source={menuId}
        buttonStyle="tertiary"
        icon="overflow"
        width="24"
        height="24"
        onClick={e => {
          e.stopPropagation();
          setMenuVisible(!menuVisible);
        }}
        {...buttonOverrides}
      />
    );
  }

  const memberCountTextThemeVals = {
    [SpaceThemeNames.classic]: { color: "grayDarker" },
    [SpaceThemeNames.scher]: { color: "white" },
  };

  function renderMemberCount() {
    const overrides = memberCountTextThemeVals[themeName];
    return (
      <Text
        id="member-count"
        my="none"
        p="none"
        font="ui.small"
        flex="0 0 auto"
        truncate
        {...overrides}>
        {kit.counts.members}
        {pluralize(" user", kit.counts.members)}
      </Text>
    );
  }

  const descriptionThemeVals = {
    [SpaceThemeNames.scher]: { color: "white" },
  };

  function renderDescription() {
    const overrides = descriptionThemeVals[themeName];
    return (
      <KitDescription id="kit-description" mt="xs" {...overrides}>
        {kit.description}
      </KitDescription>
    );
  }

  function renderPortalPills() {
    if (!kitPortals?.length) return null;
    return (
      <Flex flexDirection="row" gap="4px" pt="s" alignItems="center" flexWrap="wrap" mt="auto">
        <Text color="grayDarkest" font="ui.smallBold">
          Portals:
        </Text>
        <Flex gap="4px" maxWidth="600px" flexWrap="wrap">
          {kitPortals.map(portal => (
            <PillWrapper key={portal.id} onClick={e => handlePillClick(e, kit, portal)}>
              <Text truncate font="ui.small">
                {portal.name}
              </Text>
            </PillWrapper>
          ))}
        </Flex>
      </Flex>
    );
  }

  const normalKitCardThemeVals = {
    [SpaceThemeNames.classic]: {
      contentContainerOverrides: {
        mt: "s",
      },
      imageViewOverrides: {
        borderRadius: "small",
      },
    },
    [SpaceThemeNames.wyman]: {
      kitCardWrapperOverrides: {
        border: "default",
        borderRadius: "large",
      },
      imageViewOverrides: {
        mt: "s",
        mx: "s",
        borderRadius: "medium",
      },
      contentContainerOverrides: {
        m: "m",
      },
    },
  };

  function renderNormalKitCard() {
    const { kitCardWrapperOverrides, imageViewOverrides, contentContainerOverrides } =
      normalKitCardThemeVals[themeName];
    return (
      <KitCardWrapper
        ref={dragRef}
        data-testid={id}
        data-kitid={id}
        width="100%"
        onClick={handleClick}
        onDoubleClick={onDoubleClick}
        {...kitCardWrapperOverrides}
        {...selectionProps}>
        <Box width="100%">
          <ImageView
            css={{ cursor: onClick ? "pointer" : null }}
            src={kit.images.cover}
            icon="content.kit"
            alt={kit.name}
            aspectRatio={63}
            cover
            background="grayLighter"
            border="default"
            {...imageViewOverrides}
          />
          {themeName === SpaceThemeNames.classic && (
            <SelectionBorder borderRadius="default" isSelected={isSelected} />
          )}
        </Box>
        <Box {...contentContainerOverrides}>
          <Flex justifyContent="space-between">
            <Text as="h2" id="kit-title" font="ui.regularBold" truncate onClick={handleClick}>
              {kit.name}
            </Text>
            <Flex>
              <Icon id={`privacy-${kit.privacy}`} iconId={iconMap[kit.privacy]} size="24" />
              {canFavorite && renderFavoriteButton()}
              {menuItems && renderOverflowButton()}
              {menu}
            </Flex>
          </Flex>
          {canViewMembers && renderMemberCount()}
          {kit.description && renderDescription()}
        </Box>
        {themeName === SpaceThemeNames.wyman && (
          <SelectionBorder borderRadius="large" isSelected={isSelected} />
        )}
      </KitCardWrapper>
    );
  }

  function renderOverlayTypeKitCard() {
    return (
      <KitCardWrapper
        ref={dragRef}
        data-testid={id}
        data-kitid={id}
        width="100%"
        showOverlay={menuVisible}
        onClick={handleClick}
        onDoubleClick={onDoubleClick}
        {...selectionProps}>
        <ImageView
          css={{ cursor: onClick ? "pointer" : null }}
          src={kit.images.cover}
          icon="content.kit"
          alt={kit.name}
          aspectRatio={63}
          cover
          background="grayLighter"
          border="default"
        />
        <Flex
          className="hover"
          width="100%"
          pt="l"
          px="m"
          pb="s"
          flexDirection="column"
          position="absolute"
          bottom="0"
          textAlign="left">
          <Flex width="100%" justifyContent="space-between">
            <Text
              as="h2"
              id="kit-title"
              font="ui.regularSemiBold"
              color="white"
              truncate
              onClick={handleClick}>
              {kit.name}
            </Text>
            <Flex>
              <Icon
                id={`privacy-${kit.privacy}`}
                iconId={iconMap[kit.privacy]}
                size="24"
                fill="white"
              />
              {canFavorite && renderFavoriteButton()}
              {menuItems && renderOverflowButton()}
              {menu}
            </Flex>
          </Flex>
          {canViewMembers && renderMemberCount()}
          {kit.description && renderDescription()}
        </Flex>
        <SelectionBorder isSelected={isSelected} />
      </KitCardWrapper>
    );
  }

  function renderFullWidthKitCard() {
    const { imageViewOverrides } = normalKitCardThemeVals[themeName];
    return (
      <ResponsiveKitCardWrapper
        ref={dragRef}
        data-testid={id}
        data-kitid={id}
        width="100%"
        showOverlay={menuVisible}
        shadowOnHover={true}
        onClick={handleClick}
        onDoubleClick={onDoubleClick}
        border="default"
        p="m"
        borderRadius="medium"
        {...selectionProps}>
        <ResponsiveGrid
          width="100%"
          templateColumns="minmax(auto, 144px) minmax(200px, 4fr)"
          gap="12px">
          <ResponsiveImageWrapper alignItems="flex-start">
            <ImageView
              css={{ cursor: onClick ? "pointer" : null }}
              src={kit.images.cover}
              icon="content.kit"
              alt={kit.name}
              aspectRatio={63}
              cover
              width="100%"
              background="grayLighter"
              {...imageViewOverrides}
            />
            {themeName === SpaceThemeNames.classic && (
              <SelectionBorder borderRadius="default" isSelected={isSelected} />
            )}
          </ResponsiveImageWrapper>
          <ResponsiveContentWrapper width="100%" flexDirection="column" overflow="hidden" gap="s">
            <Flex justifyContent="space-between" gap="16px">
              <Flex flexDirection="column" gap="16px">
                <Box>
                  <Text as="h2" id="kit-title" font="ui.regularBold" truncate onClick={handleClick}>
                    {kit.name}
                  </Text>
                  <Flex gap="4px">
                    {canViewMembers && (
                      <>
                        {renderMemberCount()}
                        <Text my="none" p="none" font="ui.small" color="grayLight">
                          /
                        </Text>
                      </>
                    )}
                    <Text
                      my="none"
                      p="none"
                      font="ui.small"
                      color="grayDarker"
                      truncate
                      flex="0 2 auto">
                      Updated {formatDistanceToNow(new Date(kit.dateUpdated), { addSuffix: true })}
                    </Text>
                  </Flex>
                </Box>
              </Flex>
              <Flex gap="16px">
                <Icon id={`privacy-${kit.privacy}`} iconId={iconMap[kit.privacy]} size="24" />
                {canFavorite && renderFavoriteButton()}
                {menuItems && renderOverflowButton()}
                {menu}
              </Flex>
            </Flex>
            {kit.description && (
              <Box maxWidth="480px">
                <KitDescription id="kit-description" font="ui.regular">
                  {kit.description}
                </KitDescription>
              </Box>
            )}
            {renderPortalPills()}
          </ResponsiveContentWrapper>
        </ResponsiveGrid>
      </ResponsiveKitCardWrapper>
    );
  }

  if (fullWidth) {
    return renderFullWidthKitCard();
  }

  switch (themeName) {
    case SpaceThemeNames.classic:
    case SpaceThemeNames.wyman: {
      return renderNormalKitCard();
    }
    case SpaceThemeNames.scher: {
      return renderOverlayTypeKitCard();
    }
  }
};

export default KitCard;
